import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

import {
  CompetitionSessionError,
  logoutCompetitionSession,
  restoreCompetitionSession,
} from '@/api/competitionApi';
import { useCompetitionStore } from '@/store/competitionStore';

import type { VerificationResponse } from '@/types';

type RestoreStatus = 'idle' | 'loading' | 'ready' | 'denied';

interface RestoreOptions {
  redirectOnSuccess?: (data: VerificationResponse) => string | null;
}

/**
 * Restores competition session from HttpOnly cookie via GET /sesja.
 */
export function useCompetitionSessionRestore(options?: RestoreOptions) {
  const navigate = useNavigate();
  const setVerification = useCompetitionStore((s) => s.setVerification);
  const reset = useCompetitionStore((s) => s.reset);
  const redirectOnSuccessRef = useRef(options?.redirectOnSuccess);
  redirectOnSuccessRef.current = options?.redirectOnSuccess;

  const isDevPreview =
    import.meta.env.DEV && new URLSearchParams(window.location.search).has('preview');

  const [status, setStatus] = useState<RestoreStatus>(
    isDevPreview ? 'ready' : 'idle',
  );

  useEffect(() => {
    if (isDevPreview) {
      setStatus('ready');
      return;
    }

    let cancelled = false;
    setStatus('loading');

    void (async () => {
      try {
        const data = await restoreCompetitionSession();
        if (cancelled) return;

        setVerification(
          data.serverNow,
          data.startTime,
          data.durationMs,
          data.savedAnswers,
          data.inProgress,
          data.stage,
        );
        setStatus('ready');

        const target = redirectOnSuccessRef.current?.(data);
        if (target) {
          navigate(target, { replace: true });
        }
      } catch (err) {
        if (cancelled) return;
        reset();
        setStatus('denied');

        if (err instanceof CompetitionSessionError) {
          if (err.code === 'DEADLINE_EXCEEDED') {
            toast.error('Czas na ukończenie testu minął.');
          } else if (err.code === 'ALREADY_COMPLETED') {
            toast.error('Ten test został już zakończony.');
          }
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isDevPreview, setVerification, reset, navigate]);

  return { status, isDevPreview };
}

export async function endCompetitionSession(): Promise<void> {
  try {
    await logoutCompetitionSession();
  } catch {
    // Best-effort — cookie may already be cleared by submit
  }
  useCompetitionStore.getState().reset();
}

/** Toast, clear session, redirect home — used when test ended in another tab/browser. */
export async function exitCompetitionToHome(
  reason: 'completed' | 'deadline',
): Promise<void> {
  if (reason === 'completed') {
    toast.success('Test został ukończony.');
  } else {
    toast.error('Czas na ukończenie testu minął.');
  }
  await endCompetitionSession();
}
